import { PlatformLocation } from '@angular/common';
import { Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../shared/base-components/base.component';
import { CustomerName } from '../models/customer-name.model';
import { CustomerIndividualNameSuffixType } from '../models/enums/customer-individual-name-suffix-type.enum';
import { CustomerNameType } from '../models/enums/customer-name-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';

export class CustomerNameContext {
    constructor(
        public nameId: number,
        public customerNumberId: number,
        public customerTypeId: number,
        public isAka: boolean
    ) {
    }
}

@Component({
    selector: 'customer-name',
    templateUrl: './customer-name.html'
})
export class CustomerNameComponent extends BaseComponent {
    @Input()
    public customerNameId: number;
    @Input()
    public customerNumberId: number;
    @Input()
    public customerTypeId: number;
    @Input()
    public isAka: boolean;

    public customerNameTypes: any[];
    public customerSuffixTypes: any[];
    public nameForm: UntypedFormGroup;
    public customerType: string;
    public disableSaveButton = false;

    public get customerNameTypeId(): number {
        return this.nameForm.get('nameTypeId').value;
    }

    private nameTypes = new CustomerNameType();
    private suffixTypes = new CustomerIndividualNameSuffixType();

    constructor(
        public location: PlatformLocation,
        public formBuilder: UntypedFormBuilder,
        public service: CustomerManageService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.nameForm = this.formBuilder.group({
            nameTypeId: [null],
            individual: this.formBuilder.group({
                firstName: [''],
                middleName: [''],
                lastName: [''],
                suffixId: [null]
            }),
            organization: this.formBuilder.group({
                organizationName: ['']
            })
        }, {
            validator: Validators.compose([this.formDirtyValidator])
        });
        this.nameForm.get('nameTypeId').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe(v => this.nameTypeChanged(v));

        if (this.isAka || (!this.isAka && this.customerTypeId == 1)) {
            this.nameForm.get('nameTypeId').setValue(this.nameTypes.AKA.id);
        } else {
            this.nameForm.get('nameTypeId').setValue(this.nameTypes.DBA.id);
        }

        this.customerNameTypes = this.nameTypes.GetActive();
        this.customerNameTypes = this.filterNameTypes();
        this.customerSuffixTypes = this.suffixTypes.GetActive();

        if (this.customerNameId !== undefined && this.customerNameId != 0) {
            this.initName();
        }
    }

    private filterNameTypes = (): any[] => this.customerNameTypes.filter(nameType => (nameType.id == this.nameTypes.AKA.id && this.customerTypeId != 2) || (nameType.id == this.nameTypes.DBA.id && this.customerTypeId != 1));

    private initName = (): void => {
        this.service.getCustomerName(this.customerNameId)
            .subscribe({
                next: (result: CustomerName) => {
                    this.nameForm.get('nameTypeId').setValue(result.customerNameTypeId);
                    this.nameForm.get('individual.firstName').setValue(result.firstName);
                    this.nameForm.get('individual.middleName').setValue(result.middleName);
                    this.nameForm.get('individual.lastName').setValue(result.lastName);
                    this.nameForm.get('individual.suffixId').setValue(result.customerIndividualNameSuffixTypeId);
                    this.nameForm.get('organization.organizationName').setValue(result.organizationName);
                },
                error: e => {
                    this.handleValidationResult(e);
                }
            });
    };

    public nameTypeChanged = (type: number): void => {
        let lastNameControl = this.nameForm.get('individual.lastName');
        let organizationNameControl = this.nameForm.get('organization.organizationName');

        if (type == this.nameTypes.LegalName.id || (type == this.nameTypes.AKA.id && this.customerTypeId == 1)) {
            lastNameControl.setValidators(Validators.required);
            organizationNameControl.setValidators(null);
            lastNameControl.updateValueAndValidity();
            organizationNameControl.updateValueAndValidity();
        } else if (type == this.nameTypes.OrganizationName.id || type == this.nameTypes.DBA.id || (this.customerTypeId == 2 && type == this.nameTypes.AKA.id)) {
            lastNameControl.setValidators(null);
            organizationNameControl.setValidators(Validators.required);
            lastNameControl.updateValueAndValidity();
            organizationNameControl.updateValueAndValidity();
        }
    };

    /**
     * Makes it so the form is not valid without changing something
     * @param group
     * @returns
     */
    private formDirtyValidator(group: UntypedFormGroup): { [key: string]: boolean } {
        if (!group.dirty) {
            return { 'formNotDirty': true };
        }
    }

    public saveName = (): Observable<any> => {
        if (this.nameForm.valid) {
            let model = this.getModel();
            return this.service.saveName(model);
        }
    };

    private getModel = (): CustomerName => {
        let model = new CustomerName();
        model.customerNumberId = this.customerNumberId;
        model.customerNameId = this.customerNameId;
        model.customerNameTypeId = this.nameForm.get('nameTypeId').value;
        model.firstName = this.nameForm.get('individual.firstName').value;
        model.middleName = this.nameForm.get('individual.middleName').value;
        model.lastName = this.nameForm.get('individual.lastName').value;
        model.customerIndividualNameSuffixTypeId = this.nameForm.get('individual.suffixId').value;
        model.organizationName = this.nameForm.get('organization.organizationName').value;

        return model;
    };
}
